import { useState, useRef, useEffect } from 'react'
import { fhirExportService } from '../services/fhirExportService'
import './PatientFHIRExport.css'

interface PatientFHIRExportProps {
  patientId: string
}

/**
 * Patient FHIR Export Component
 * Provides UI for exporting patient records in FHIR format with two options:
 * 1. Full export (with PII)
 * 2. Masked export (PII masked)
 */
export function PatientFHIRExport({ patientId }: PatientFHIRExportProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside or pressing ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showMenu) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [showMenu])

  const handleExport = async (maskPII: boolean) => {
    if (!patientId) {
      setExportError('Patient ID is required')
      return
    }

    setIsExporting(true)
    setExportError(null)
    setShowMenu(false)

    try {
      await fhirExportService.exportPatientToFHIR(patientId, maskPII)
      // Clear any previous errors on success
      setExportError(null)
    } catch (error: any) {
      console.error('Export error:', error)
      setExportError(error.message || 'Failed to export patient record')
      // Auto-hide error after 5 seconds
      setTimeout(() => setExportError(null), 5000)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="fhir-export-container" ref={containerRef}>
      <button
        className="btn-secondary fhir-export-btn"
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExporting}
        aria-label="Export patient record"
        aria-expanded={showMenu}
        aria-haspopup="true"
      >
        <span>{isExporting ? 'Exporting...' : 'Export FHIR'}</span>
        <svg
          className={`fhir-export-chevron ${showMenu ? 'open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {showMenu && (
        <div className="fhir-export-menu" ref={menuRef}>
          <div className="fhir-export-menu-header">
            <span>Export Options</span>
          </div>
          <button
            className="fhir-export-menu-item"
            onClick={() => handleExport(false)}
            disabled={isExporting}
          >
            <div className="fhir-export-menu-item-content">
              <div className="fhir-export-menu-item-title">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 1V11M8 11L5 8M8 11L11 8M3 13H13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Export with PII
              </div>
              <div className="fhir-export-menu-item-description">
                Contains all patient information
              </div>
            </div>
          </button>
          <button
            className="fhir-export-menu-item"
            onClick={() => handleExport(true)}
            disabled={isExporting}
          >
            <div className="fhir-export-menu-item-content">
              <div className="fhir-export-menu-item-title">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 1V11M8 11L5 8M8 11L11 8M3 13H13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.7"
                  />
                  <path
                    d="M2 2L14 14"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    opacity="0.5"
                  />
                </svg>
                Export with Masked PII
              </div>
              <div className="fhir-export-menu-item-description">
                PII partially masked for privacy
              </div>
            </div>
          </button>
        </div>
      )}

      {exportError && (
        <div className="fhir-export-error">
          {exportError}
        </div>
      )}
    </div>
  )
}




